import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import LocalStorage from '@/helpers/localStorage';
import { addMinutes } from '@/helpers/dateHelper';
import type { Cart } from '@/types/cart';
import { STORAGE_KEYS } from '@/constants/storageKeys';

const CHECKOUT_CONTEXT_TTL_MINUTES = 60 * 24; // 24h

export interface CheckoutContext {
  sale?: Record<string, unknown>;
  tenantName?: string;
  tenantUrl?: string;
  specialistName?: string;
  therapist?: Record<string, unknown>;
  type?: string;
  isOnline?: boolean;
  durationLabel?: string;
  slot?: Record<string, unknown>;
  [key: string]: unknown;
}

function loadCheckoutContextFromStorage(): CheckoutContext | null {
  if (typeof window === 'undefined') return null;

  const stored = LocalStorage.getWithTimeout(STORAGE_KEYS.CHECKOUT_CONTEXT);
  if (stored && typeof stored === 'object') {
    return stored as CheckoutContext;
  }
  return null;
}

export const useCheckoutStore = defineStore('checkout', () => {
  // State
  const checkoutContext = ref<CheckoutContext | null>(
    loadCheckoutContextFromStorage()
  );
  const cart = ref<Cart | null>(null);

  // Computed
  const hasContext = computed(() => checkoutContext.value !== null);
  const hasCart = computed(() => cart.value !== null && (cart.value.sales?.length ?? 0) > 0);

  // Actions
  const setCheckoutContext = (data: CheckoutContext | null) => {
    checkoutContext.value = data;
    
    if (typeof window === 'undefined') return;
    
    if (data) {
      const expires = addMinutes(new Date(), CHECKOUT_CONTEXT_TTL_MINUTES);
      LocalStorage.addWithTimout(STORAGE_KEYS.CHECKOUT_CONTEXT, data, expires);
    } else {
      LocalStorage.delete(STORAGE_KEYS.CHECKOUT_CONTEXT);
    }
  };

  const setCart = (data: Cart | null) => {
    cart.value = data ?? null;
  };

  const clearCheckout = () => {
    checkoutContext.value = null;
    cart.value = null;
    LocalStorage.delete(STORAGE_KEYS.CHECKOUT_CONTEXT);
  };

  return {
    // State
    checkoutContext,
    cart,
    
    // Computed
    hasContext,
    hasCart,
    
    // Actions
    setCheckoutContext,
    setCart,
    clearCheckout,
  };
});
